import React, {useEffect, useState} from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBookMedical, faDownload, faEye} from '@fortawesome/free-solid-svg-icons';
import {useBaseUrl} from '../utils/utils';
import localForage from 'localforage';
import Swal from 'sweetalert2';

interface Resource {
    id: number;
    title: string;
    description: string;
    file_type: string;
    view_count: number;
    download_count: number;
}

const PatientResources: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [resources, setResources] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Health Resources - Uzima Healthcare";
        (async () => {
            try {
                const token = await localForage.getItem<string>('access_token');
                const response = await fetch(`${baseUrl}resources/api/v1/library/`, {
                    headers: {Authorization: token ? `Bearer ${token}` : ''},
                });
                const data = await response.json();
                setResources(data.results || data || []);
            } catch (error) {
                console.error('Error fetching resources:', error);
                Swal.fire('Error', 'Failed to load health resources', 'error');
            } finally {
                setLoading(false);
            }
        })();
    }, [baseUrl]);

    return (
        <PatientDashboardLayout>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h2 className="mb-1">
                            <FontAwesomeIcon icon={faBookMedical} className="me-2 text-primary"/>
                            Health Resources
                        </h2>
                        <p className="text-muted">Educational materials published by your care providers</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : resources.length === 0 ? (
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <FontAwesomeIcon icon={faBookMedical} size="3x" className="text-muted mb-3"/>
                            <h5>No Resources Available Yet</h5>
                            <p className="text-muted">Guides and materials shared by your clinic will appear here.</p>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        {resources.map((resource) => (
                            <div key={resource.id} className="col-md-6 col-lg-4 mb-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h5 className="card-title mb-1">{resource.title}</h5>
                                        <span className="badge bg-soft-primary text-primary mb-2">{resource.file_type}</span>
                                        <p className="text-muted small">{resource.description}</p>
                                        <div className="d-flex gap-3 small text-muted">
                                            <span><FontAwesomeIcon icon={faEye} className="me-1"/>{resource.view_count}</span>
                                            <span><FontAwesomeIcon icon={faDownload} className="me-1"/>{resource.download_count}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientResources;
